/**
 * Edge-health overlay style — the visual an edge gets on the live board from its
 * telemetry. Pure: maps an edge's stat (or its absence) to a stroke overlay.
 *
 *   - error  (errorRate ≥ threshold): a red overlay, brighter the worse it is
 *   - unobserved (modeled edge, no live traffic): a dim dashed ghost
 *   - confirmed & healthy: null (no overlay — the particle flow already conveys
 *     the rate; we don't clutter a healthy edge)
 *
 * Only meaningful while telemetry is flowing; the layer no-ops when the stats
 * snapshot is empty so a doc with no live feed isn't drawn all-dashed.
 */
import type { EdgeStat } from "../liveStatsStore.js";

export interface EdgeHealthStyle {
  readonly stroke: string;
  readonly width: number;
  readonly dash?: string;
  readonly opacity: number;
  readonly kind: "error" | "unobserved";
}

/** Error-rate at/above which an edge reads as failing. */
export const EDGE_ERROR_THRESHOLD = 0.01; // 1%
/** Error-rate that saturates the red intensity ramp. */
const EDGE_ERROR_SATURATION = 0.1; // 10%

const RED = "#f43f5e";
const SLATE = "#64748b";

export function edgeHealthStyle(stat: EdgeStat | undefined): EdgeHealthStyle | null {
  // Modeled edge with no live traffic → unobserved ghost.
  if (!stat || stat.perSec <= 0) {
    return { stroke: SLATE, width: 2, dash: "5 5", opacity: 0.3, kind: "unobserved" };
  }
  // Erroring → red, intensity scaled by how bad.
  if (stat.errorRate !== undefined && stat.errorRate >= EDGE_ERROR_THRESHOLD) {
    const sev = Math.min(1, stat.errorRate / EDGE_ERROR_SATURATION);
    return { stroke: RED, width: 3, opacity: 0.45 + 0.45 * sev, kind: "error" };
  }
  // Confirmed + healthy → no overlay.
  return null;
}
